// Module: ModerationCheckpointScreen
// Role: BUG-1590 — keyed launcher for a standalone MODERATION checkpoint. Starts
//       the review through the existing idempotent submit, then delegates the
//       pending/escalated queue state to ModerationStatus. Launch and subject
//       errors are visible and retryable.
// Uses: flow-types, ModerationStatus, Frame, WizardCtaBar, tokens
// Used by: FlowScreen (MODERATION branch)

import { useEffect, useRef } from 'react';
import type { Checkpoint, CheckpointAttempt, SlotFlowRun } from '../../api/flow-types';
import { ModerationStatus } from './templates/ModerationStatus';
import { Frame } from './atoms/Frame';
import { WizardCtaBar } from '../../components/atoms/WizardCtaBar';
import { G, P, S, T } from '../../styles/tokens';

interface Props {
  checkpoint: Checkpoint;
  attempt?: CheckpointAttempt;
  flowRun: SlotFlowRun;
  checkpoints: Checkpoint[];
  /** Submits the current checkpoint through the existing idempotent endpoint. */
  onCta: () => void;
  /** Lab-only dev decision for an already escalated attempt. */
  onDevAccept: () => void;
  devAcceptError: Error | null;
  submitError: Error | null;
  onLeaveHome?: () => void;
}

function subjectFailure(attempt?: CheckpointAttempt): boolean {
  const payload = attempt?.verdict_payload ?? {};
  const value = (payload as Record<string, unknown>).moderation_subject_error;
  return typeof value === 'object' && value !== null;
}

function ErrorCard({
  title,
  body,
  onRetry,
  onLeaveHome,
}: {
  title: string;
  body: string;
  onRetry: () => void;
  onLeaveHome?: () => void;
}) {
  return (
    <Frame>
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          padding: '0 24px',
          textAlign: 'center',
        }}
      >
        <div style={{ font: T.headline, color: S.label }}>{title}</div>
        <div
          style={{
            font: T.footnote,
            color: S.secondaryLabel,
            lineHeight: 1.5,
            maxWidth: 280,
          }}
        >
          {body}
        </div>
      </div>
      <WizardCtaBar label="Попробовать снова" onClick={onRetry} />
      {onLeaveHome && <WizardCtaBar label="На главную" onClick={onLeaveHome} kind="ink" />}
    </Frame>
  );
}

function ErrorBanner({ error }: { error: Error }) {
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 80,
        left: 16,
        right: 16,
        padding: '12px 16px',
        borderRadius: 14,
        ...G.errorTag,
        zIndex: 50,
      }}
    >
      <p style={{ font: T.footnote, color: P.danger, margin: 0, textAlign: 'center' }}>
        {error.message}
      </p>
    </div>
  );
}

function LaunchingCard() {
  return (
    <Frame>
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          padding: '0 24px',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 24,
            border: '4px solid rgba(226,114,91,0.18)',
            borderTopColor: P.terracotta,
            animation: 'spin 1s linear infinite',
          }}
        />
        <span style={{ font: T.footnote, color: P.muted }}>Отправляем на проверку…</span>
      </div>
    </Frame>
  );
}

export function ModerationCheckpointScreen({
  checkpoint,
  attempt,
  flowRun,
  checkpoints,
  onCta,
  onDevAccept,
  devAcceptError,
  submitError,
  onLeaveHome,
}: Props) {
  const key = `${flowRun.id}:${checkpoint.id}`;
  const human = checkpoint.metadata?.moderation_mode === 'human';
  const onCtaRef = useRef(onCta);
  onCtaRef.current = onCta;
  const launchedRef = useRef<string | null>(null);

  // Fresh step: launch once per mount. Remounts and StrictMode still hit the
  // server's idempotent submit — the local ref is only a duplicate-call guard,
  // never a substitute for server-side idempotency.
  useEffect(() => {
    if (attempt) return;
    if (launchedRef.current === key) return;
    launchedRef.current = key;
    onCtaRef.current();
  }, [key, attempt]);

  if (attempt?.verdict === 'escalated_to_moderation') {
    return (
      <>
        <ModerationStatus
          checkpoint={checkpoint}
          attempt={attempt}
          flowRun={flowRun}
          checkpoints={checkpoints}
          onCta={onDevAccept}
          manual
          onLeaveHome={onLeaveHome}
        />
        {devAcceptError && <ErrorBanner error={devAcceptError} />}
      </>
    );
  }

  if (attempt?.verdict === 'pending') {
    if (subjectFailure(attempt)) {
      return (
        <ErrorCard
          title="Не удалось начать проверку"
          body="Видео для проверки не найдено. Попробуйте ещё раз — если ошибка повторится, напишите в поддержку."
          onRetry={onCta}
          onLeaveHome={onLeaveHome}
        />
      );
    }
    return (
      <ModerationStatus
        checkpoint={checkpoint}
        attempt={attempt}
        flowRun={flowRun}
        checkpoints={checkpoints}
        onCta={onCta}
        manual={human}
        onLeaveHome={onLeaveHome}
      />
    );
  }

  if (submitError) {
    return (
      <ErrorCard
        title="Не удалось отправить на проверку"
        body="Проверьте соединение и попробуйте ещё раз. Если ошибка повторится, напишите в поддержку."
        onRetry={onCta}
        onLeaveHome={onLeaveHome}
      />
    );
  }

  return <LaunchingCard />;
}
